import { useEffect, useRef, useState } from 'react';

/** How often live mode asks Loki for newer log lines. */
export const LIVE_INTERVAL_MS = 20_000;

interface UseLiveReloadOptions {
  isLive: boolean;
  logQueryDone: boolean;
  hasEntries: boolean;
  chartZoomRange: { from: number; to: number } | null;
  fetchNewerLogs: () => Promise<void>;
}

export function useLiveReload({
  isLive,
  logQueryDone,
  hasEntries,
  chartZoomRange,
  fetchNewerLogs,
}: UseLiveReloadOptions) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Epoch ms of the next automatic fetch. Null while live polling is off. */
  const [nextFetchAt, setNextFetchAt] = useState<number | null>(null);

  useEffect(() => {
    const clearTimer = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    if (isLive && !chartZoomRange && logQueryDone && hasEntries) {
      const armNextFetch = () => setNextFetchAt(Date.now() + LIVE_INTERVAL_MS);
      fetchNewerLogs();
      armNextFetch();
      intervalRef.current = setInterval(() => {
        fetchNewerLogs();
        armNextFetch();
      }, LIVE_INTERVAL_MS);
      return clearTimer;
    }

    clearTimer();
    setNextFetchAt(null);
    return clearTimer;
  }, [isLive, logQueryDone, chartZoomRange, hasEntries, fetchNewerLogs]);

  return { nextFetchAt };
}
